import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { useAuth } from 'contexts/AuthContext';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import { Card, CommonCss, PageContainer, PageTitle } from 'styles/common.styles';
import {
  Meta,
  Guide,
  ModeTabBar,
  ModeTab,
} from 'components/system/SystemPageChrome.styles';

import { ActionButtonGroup, CollapsibleSearchPanel, FormArea } from 'components/forms';
import { HiGridComponent, hiCol, useHiGridHook } from 'components/grid/hiGrid';
import {
  ActiveGridHeader,
  ActiveGridProvider,
  ActiveGridScope,
  useActiveGrid,
} from 'contexts/ActiveGridContext';
import {
  GridExportFocusScope,
  GridExportProvider,
  useExcelExportAction,
  useRegisterGridExport,
} from 'components/grid/excel';
import CodeLookupDialog from 'components/budget/CodeLookupDialog';
import { showAlert } from 'utils/toastBridge';
import { loadDepts, getCachedDepts } from 'master/deptStore';
import {
  ACCOUNT_STATUS_OPTIONS,
  enrichUserRows,
  filterUsers,
  getCachedUsers,
  loadUsers,
} from 'master/userAccountStore';
import { buildDeptAuthProgramTree, listDeptAuthPrograms } from 'master/deptAuthProgramCatalog';
import {
  DEPT_MENU_LEVEL_OPTIONS,
  USER_AUTH_MODULE_OPTIONS,
  USER_SCOPE_OPTIONS,
  applyDeptFullImplication,
  buildAdminPermissionMatrix,
  buildAdminScreenTree,
  bulkSetDeptMenuLevel,
  bulkSetDeptScreenTree,
  collectGrantedAdminScreens,
  countGrantedScreensInTree,
  joinDeptNames,
  loadAdminPermissions,
  loadAdminScreenPermissions,
  saveAdminPermissions,
  saveAdminScreenPermissions,
  syncDeptGrantChange,
  syncDeptModuleScope,
  syncDeptScreenParentGrants,
  validateAdminPermissions,
  validateAdminScreenPermissions,
} from 'master/permissionStore';

const USER_GRID_ID = 'admin-perm-user';
const SCREEN_GRID_ID = 'admin-perm-screen';
const PERMISSION_GRID_ID = 'admin-perm-module';
const SEARCH_LABEL_WIDTH = '56px';
const MATRIX_SIZE = USER_AUTH_MODULE_OPTIONS.length * DEPT_MENU_LEVEL_OPTIONS.length;

function fingerprintAdminPerms(rows = [], accountUid) {
  return (rows || [])
    .filter((r) => r && r.accountUid === accountUid && r.grantedYn === 'Y' && r.mod !== 'D')
    .map(
      (r) =>
        [
          r.moduleCode,
          r.menuLevel,
          r.scopeType || 'OWN',
          [...(r.deptUids || [])].map(String).sort().join(','),
          String(r.remark || '').trim(),
        ].join('|')
    )
    .sort()
    .join(';');
}

function fingerprintAdminScreens(rows = [], accountUid) {
  return (rows || [])
    .filter((r) => r && r.accountUid === accountUid && r.grantedYn === 'Y' && r.mod !== 'D')
    .map((r) => r.screenId)
    .sort()
    .join(';');
}

function editStatus(baselineFp, currentFp) {
  if (currentFp === baselineFp) return { mod: '' };
  return { mod: baselineFp ? 'U' : 'A' };
}

const Split = styled.div`
  display: flex;
  gap: 8px;
  flex: 1;
  min-height: 0;
`;

const Panel = styled(Card)`
  display: flex;
  flex: ${(p) => p.$flex || 1};
  flex-direction: column;
  min-width: 0;
  min-height: 0;
  padding: 0;
  overflow: hidden;
`;

const GridBody = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
`;





const RightStack = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  flex-direction: column;
`;

/**
 * 관리자권한설정
 * - 좌측: 사용자 / 우측: 화면구성(승인·관리 화면 트리) · 권한구성(모듈×조회·등록·전체)
 * - 예외(버튼 단위)는 예외권한설정 화면에서 처리
 */
function AdminPermissionManageInner() {
  const { user, isMaster } = useAuth();
  const tenantCode = useTenantCode();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);
  const userGridRef = useRef(null);
  const screenGridRef = useRef(null);
  const permissionGridRef = useRef(null);
  const selectedAccountUidRef = useRef('');
  const allPermissionsRef = useRef([]);
  const allScreensRef = useRef([]);
  const baselinePermRef = useRef(new Map());
  const baselineScreenRef = useRef(new Map());
  const lookupTargetRef = useRef('');
  const [activeMode, setActiveMode] = useState('screen');
  const [screenAllOn, setScreenAllOn] = useState(false);
  const [permReadOn, setPermReadOn] = useState(false);
  const [permRegOn, setPermRegOn] = useState(false);
  const [permFullOn, setPermFullOn] = useState(false);
  const [selectedHint, setSelectedHint] = useState(
    '사용자를 선택하면 관리자 화면·권한구성을 설정할 수 있습니다.'
  );
  const [deptLookupOpen, setDeptLookupOpen] = useState(false);
  const { activeGridId } = useActiveGrid();

  const programTree = useMemo(() => buildDeptAuthProgramTree(), []);
  const programCatalog = useMemo(() => listDeptAuthPrograms(), []);
  const programCount = programCatalog.length;

  const screenTreeOptions = useMemo(
    () => ({
      idKey: 'id',
      childrenKey: 'subRows',
      checkboxField: 'grantedYn',
      checkboxTrueValue: 'Y',
      checkboxFalseValue: 'N',
    }),
    []
  );

  const searchDefaults = useMemo(
    () => ({ tenantCode, loginId: '', userNm: '', status: 'ACTIVE' }),
    [tenantCode]
  );
  const methods = useForm({ mode: 'onSubmit', defaultValues: searchDefaults });
  const { getValues, setValue } = methods;

  useEffect(() => {
    setValue('tenantCode', tenantCode);
  }, [setValue, tenantCode]);

  const [deptRows, setDeptRows] = useState(() =>
    (getCachedDepts(tenantCode) || []).filter((r) => r.useYn !== 'N')
  );
  useEffect(() => {
    let cancelled = false;
    loadDepts(tenantCode)
      .then((rows) => {
        if (!cancelled) setDeptRows((rows || []).filter((r) => r.useYn !== 'N'));
      })
      .catch(() => {
        if (!cancelled) setDeptRows([]);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantCode]);

  const rebuildBaseline = useCallback((permRows, screenRows) => {
    const uids = new Set([
      ...(permRows || []).map((r) => r.accountUid).filter(Boolean),
      ...(screenRows || []).map((r) => r.accountUid).filter(Boolean),
      ...((userGridRef.current?.getData?.() || []).map((u) => u.accountUid).filter(Boolean)),
    ]);
    const pMap = new Map();
    const sMap = new Map();
    uids.forEach((uid) => {
      pMap.set(uid, fingerprintAdminPerms(permRows, uid));
      sMap.set(uid, fingerprintAdminScreens(screenRows, uid));
    });
    baselinePermRef.current = pMap;
    baselineScreenRef.current = sMap;
  }, []);

  const syncUserEditMarks = useCallback((accountUids) => {
    const targets = accountUids?.length
      ? accountUids
      : [
          ...new Set(
            (userGridRef.current?.getData?.() || []).map((r) => r.accountUid).filter(Boolean)
          ),
        ];
    if (!targets.length) return;
    const data = userGridRef.current?.getData?.() || [];
    let changed = false;
    const next = data.map((row) => {
      if (!targets.includes(row.accountUid)) return row;
      const combinedBase = `${baselinePermRef.current.get(row.accountUid) ?? ''}#${baselineScreenRef.current.get(row.accountUid) ?? ''}`;
      const combinedCur = `${fingerprintAdminPerms(allPermissionsRef.current, row.accountUid)}#${fingerprintAdminScreens(allScreensRef.current, row.accountUid)}`;
      const status = editStatus(combinedBase, combinedCur);
      if (row.mod === status.mod) return row;
      changed = true;
      return { ...row, mod: status.mod };
    });
    if (changed) userGridRef.current?.setData?.(next);
  }, []);

  const withDisplay = useCallback(
    (row) => ({ ...row, deptNamesDisp: joinDeptNames(row.deptNames) }),
    []
  );

  const commitScreenMatrix = useCallback(
    (rows) => {
      const accountUid = selectedAccountUidRef.current;
      const next = rows || [];
      screenGridRef.current?.setData?.(next);
      const grantedCount = countGrantedScreensInTree(next);
      setScreenAllOn(programCount > 0 && grantedCount >= programCount);
      if (!accountUid) return;
      const granted = collectGrantedAdminScreens(next, accountUid);
      allScreensRef.current = [
        ...allScreensRef.current.filter((r) => r.accountUid !== accountUid),
        ...granted,
      ];
      syncUserEditMarks([accountUid]);
      const userRow = (userGridRef.current?.getData?.() || []).find(
        (r) => r.accountUid === accountUid
      );
      if (userRow) {
        setSelectedHint(
          `${userRow.loginId || ''} · ${userRow.userNm || ''} · 화면 ${grantedCount}/${programCount} · 권한구성 ${MATRIX_SIZE}행`
        );
      }
    },
    [programCount, syncUserEditMarks]
  );

  const commitPermissionMatrix = useCallback(
    (rows) => {
      const accountUid = selectedAccountUidRef.current;
      const next = applyDeptFullImplication(rows || []);
      permissionGridRef.current?.setData?.(next.map(withDisplay));
      const active = next.filter((r) => r.mod !== 'D');
      const modules = USER_AUTH_MODULE_OPTIONS.map((m) => m.value);
      const levelAllOn = (level) =>
        modules.every((mc) =>
          active.some((r) => r.moduleCode === mc && r.menuLevel === level && r.grantedYn === 'Y')
        );
      setPermReadOn(levelAllOn('READ'));
      setPermRegOn(levelAllOn('REGISTER'));
      setPermFullOn(levelAllOn('FULL'));
      if (!accountUid) return;
      const granted = next
        .filter((r) => r.grantedYn === 'Y')
        .map((r) => ({ ...r, accountUid, mod: '' }));
      allPermissionsRef.current = [
        ...allPermissionsRef.current.filter((r) => r.accountUid !== accountUid),
        ...granted,
      ];
      syncUserEditMarks([accountUid]);
    },
    [syncUserEditMarks, withDisplay]
  );

  const mergeCurrentPanels = useCallback(() => {
    if (!selectedAccountUidRef.current) return;
    commitScreenMatrix(screenGridRef.current?.getData?.() || []);
    commitPermissionMatrix(permissionGridRef.current?.getData?.() || []);
  }, [commitPermissionMatrix, commitScreenMatrix]);

  const loadRightPanels = useCallback(
    (accountUid) => {
      if (!accountUid) {
        screenGridRef.current?.setData?.([]);
        permissionGridRef.current?.setData?.([]);
        return;
      }
      commitScreenMatrix(buildAdminScreenTree(accountUid, allScreensRef.current, programTree));
      commitPermissionMatrix(buildAdminPermissionMatrix(accountUid, allPermissionsRef.current));
      setTimeout(() => screenGridRef.current?.expandAll?.(), 0);
    },
    [commitPermissionMatrix, commitScreenMatrix, programTree]
  );

  const selectUser = useCallback(
    (row) => {
      if (!row?.accountUid) return;
      const prevUid = selectedAccountUidRef.current;
      if (prevUid && prevUid !== row.accountUid) mergeCurrentPanels();
      selectedAccountUidRef.current = row.accountUid;
      const grantedScreens = allScreensRef.current.filter(
        (r) => r.accountUid === row.accountUid && r.grantedYn === 'Y'
      ).length;
      setSelectedHint(
        `${row.loginId || ''} · ${row.userNm || ''} · 화면 ${grantedScreens}/${programCount} · 권한구성 ${MATRIX_SIZE}행`
      );
      loadRightPanels(row.accountUid);
      if (prevUid && prevUid !== row.accountUid) syncUserEditMarks([prevUid, row.accountUid]);
      else syncUserEditMarks([row.accountUid]);
    },
    [loadRightPanels, mergeCurrentPanels, programCount, syncUserEditMarks]
  );

  const handleModeChange = useCallback(
    (mode) => {
      if (mode === activeMode) return;
      mergeCurrentPanels();
      setActiveMode(mode);
    },
    [activeMode, mergeCurrentPanels]
  );

  const handleSearch = useCallback(() => {
    return Promise.all([
      loadAdminPermissions(tenantCode),
      loadAdminScreenPermissions(tenantCode),
      loadUsers(tenantCode, user),
    ]).then(([perms, screens, users]) => {
      allPermissionsRef.current = perms;
      allScreensRef.current = screens;
      const filtered = filterUsers(enrichUserRows(users), getValues()).map((r) => ({
        ...r,
        mod: '',
      }));
      userGridRef.current?.setData?.(filtered);
      rebuildBaseline(allPermissionsRef.current, allScreensRef.current);
      const selected = filtered.find((r) => r.accountUid === selectedAccountUidRef.current);
      if (selected) {
        selectedAccountUidRef.current = '';
        selectUser(selected);
        return;
      }
      selectedAccountUidRef.current = '';
      setSelectedHint('사용자를 선택하면 관리자 화면·권한구성을 설정할 수 있습니다.');
      screenGridRef.current?.setData?.([]);
      permissionGridRef.current?.setData?.([]);
    });
  }, [getValues, rebuildBaseline, selectUser, tenantCode, user]);

  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const userColumns = useMemo(
    () => [
      hiCol.roTextColumn({ header: '사용자ID', field: 'loginId', size: 110 }),
      hiCol.roTextColumn({ header: '사용자명', field: 'userNm', size: 100 }),
      hiCol.roTextColumn({ header: '주관부서', field: 'deptName', size: 140 }),
      hiCol.roTextColumn({ header: '계정유형', field: 'accountTypeNm', size: 100, align: 'center' }),
      hiCol.roTextColumn({ header: '계정상태', field: 'statusNm', size: 90, align: 'center' }),
    ],
    []
  );

  const screenColumns = useMemo(
    () => [
      hiCol.roTreeColumn({ header: '메뉴/화면', field: 'menuName', size: 320 }),
      hiCol.roTextColumn({ header: '화면ID', field: 'screenId', size: 140 }),
      hiCol.roTextColumn({ header: '유형', field: 'menuTypeNm', size: 70, align: 'center' }),
    ],
    []
  );

  const openDeptLookup = useCallback((row) => {
    if (!row?.adminPermUid && !row?.id) return;
    if (row.scopeType !== 'PICK') {
      showAlert('데이터범위를 「선택부서」로 지정한 행에서만 대상부서를 선택합니다.', 'warning');
      return;
    }
    lookupTargetRef.current = row.adminPermUid || row.id;
    setDeptLookupOpen(true);
  }, []);

  const grantCol = useMemo(() => {
    const base = hiCol.edCheckboxColumn({ header: '부여', field: 'grantedYn', size: 72 });
    return {
      ...base,
      meta: { ...base.meta, editable: (row) => !row?.grantLocked },
    };
  }, []);

  const permissionColumns = useMemo(
    () => [
      hiCol.roTextColumn({ header: '모듈', field: 'moduleName', size: 100 }),
      hiCol.roTextColumn({ header: '메뉴구성', field: 'menuLevelNm', size: 90, align: 'center' }),
      grantCol,
      hiCol.edComboColumn({
        header: '데이터범위',
        field: 'scopeType',
        size: 140,
        options: USER_SCOPE_OPTIONS,
        required: true,
      }),
      hiCol.edTextColumn({
        header: '선택부서',
        field: 'deptNamesDisp',
        size: 200,
        editable: false,
        showSearch: true,
        searchTitle: '대상부서 선택',
        onSearch: ({ row }) => openDeptLookup(row),
      }),
      hiCol.edTextColumn({ header: '비고', field: 'remark', size: 180 }),
    ],
    [grantCol, openDeptLookup]
  );

  const userGrid = useHiGridHook({
    gridRef: userGridRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: false,
    confirmCheck: false,
    defaultRow: {},
  });

  const screenGrid = useHiGridHook({
    gridRef: screenGridRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: true,
    confirmCheck: false,
    defaultRow: {},
    treeOptions: screenTreeOptions,
  });

  const permissionGrid = useHiGridHook({
    gridRef: permissionGridRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: true,
    confirmCheck: false,
    defaultRow: {},
  });

  useEffect(() => {
    userGrid.onSelectRow = (row) => selectUser(row);
    userGrid.onRowClicked = ({ data }) => selectUser(data);
  }, [selectUser, userGrid]);

  useEffect(() => {
    screenGrid.onAfterSaveCell = (_rowData, { field }) => {
      if (field !== 'grantedYn') return;
      commitScreenMatrix(syncDeptScreenParentGrants(screenGridRef.current?.getData?.() || []));
    };
  }, [commitScreenMatrix, screenGrid]);

  useEffect(() => {
    permissionGrid.onAfterSaveCell = (rowData, { field }) => {
      const rows = permissionGridRef.current?.getData?.() || [];
      if (field === 'grantedYn') {
        if (rowData?.grantLocked && rowData.grantedYn !== 'Y') {
          showAlert(
            '「전체」가 부여된 모듈의 조회·등록은 자동 포함됩니다. 전체를 먼저 해제하세요.',
            'warning'
          );
          commitPermissionMatrix(rows.map((r) => (r.grantLocked ? { ...r, grantedYn: 'Y' } : r)));
          return;
        }
        commitPermissionMatrix(syncDeptGrantChange(rows, rowData));
        return;
      }
      if (field === 'scopeType') {
        commitPermissionMatrix(
          syncDeptModuleScope(rows, rowData.moduleCode, {
            scopeType: rowData.scopeType,
            ...(rowData.scopeType !== 'PICK'
              ? { deptUids: [], deptCodes: [], deptNames: [], deptNamesDisp: '' }
              : {}),
          })
        );
        return;
      }
      if (field === 'remark') commitPermissionMatrix(rows);
    };
  }, [commitPermissionMatrix, permissionGrid]);

  const applyDeptLookup = useCallback(
    (dept) => {
      const target = lookupTargetRef.current;
      if (!target || !dept?.deptUid) return;
      const rows = permissionGridRef.current?.getData?.() || [];
      const index = rows.findIndex((r) => r.adminPermUid === target || r.id === target);
      if (index < 0) return;
      const row = rows[index];
      const deptUids = [...(row.deptUids || [])];
      const deptCodes = [...(row.deptCodes || [])];
      const deptNames = [...(row.deptNames || [])];
      const found = deptUids.indexOf(dept.deptUid);
      if (found >= 0) {
        deptUids.splice(found, 1);
        deptCodes.splice(found, 1);
        deptNames.splice(found, 1);
      } else {
        deptUids.push(dept.deptUid);
        deptCodes.push(dept.deptCode || '');
        deptNames.push(dept.deptName || '');
      }
      commitPermissionMatrix(
        syncDeptModuleScope(rows, row.moduleCode, {
          scopeType: 'PICK',
          deptUids,
          deptCodes,
          deptNames,
        })
      );
    },
    [commitPermissionMatrix]
  );

  const handleToggleMenuLevel = useCallback(
    (menuLevel) => {
      if (!selectedAccountUidRef.current) {
        showAlert('사용자를 먼저 선택하세요.', 'warning');
        return;
      }
      const rows = permissionGridRef.current?.getData?.() || [];
      const modules = USER_AUTH_MODULE_OPTIONS.map((m) => m.value);
      const currentlyOn = modules.every((mc) =>
        rows.some((r) => r.moduleCode === mc && r.menuLevel === menuLevel && r.grantedYn === 'Y')
      );
      const nextYn = currentlyOn ? 'N' : 'Y';
      commitPermissionMatrix(bulkSetDeptMenuLevel(rows, menuLevel, nextYn));
      const label = DEPT_MENU_LEVEL_OPTIONS.find((o) => o.value === menuLevel)?.label || menuLevel;
      showAlert(
        nextYn === 'Y'
          ? `전 모듈 「${label}」 권한을 일괄 부여했습니다.`
          : `전 모듈 「${label}」 권한을 일괄 해제했습니다.`,
        'success'
      );
    },
    [commitPermissionMatrix]
  );

  const handleToggleAllScreens = useCallback(() => {
    if (!selectedAccountUidRef.current) {
      showAlert('사용자를 먼저 선택하세요.', 'warning');
      return;
    }
    const nextOn = !screenAllOn;
    commitScreenMatrix(
      bulkSetDeptScreenTree(screenGridRef.current?.getData?.() || [], nextOn ? 'Y' : 'N')
    );
    showAlert(
      nextOn ? '모든 화면 접근을 허용했습니다.' : '모든 화면 접근을 해제했습니다.',
      'success'
    );
  }, [commitScreenMatrix, screenAllOn]);

  const handleSave = useCallback(() => {
    mergeCurrentPanels();
    const users = getCachedUsers(tenantCode);
    const permRows = allPermissionsRef.current.filter(
      (r) => r.mod !== 'D' && r.grantedYn === 'Y'
    );
    const screenRows = allScreensRef.current.filter(
      (r) => r.mod !== 'D' && r.grantedYn === 'Y'
    );
    const pVal = validateAdminPermissions(permRows, users);
    if (!pVal.ok) {
      showAlert(pVal.message, 'warning');
      return Promise.resolve();
    }
    const sVal = validateAdminScreenPermissions(screenRows, users, programCatalog);
    if (!sVal.ok) {
      showAlert(sVal.message, 'warning');
      return Promise.resolve();
    }
    return saveAdminPermissions(tenantCode, permRows)
      .then((savedPerms) => {
        allPermissionsRef.current = savedPerms;
        return saveAdminScreenPermissions(tenantCode, screenRows);
      })
      .then((savedScreens) => {
        allScreensRef.current = savedScreens;
        rebuildBaseline(allPermissionsRef.current, allScreensRef.current);
        userGridRef.current?.setData?.(
          (userGridRef.current?.getData?.() || []).map((r) => ({ ...r, mod: '' }))
        );
        if (selectedAccountUidRef.current) {
          loadRightPanels(selectedAccountUidRef.current);
          syncUserEditMarks([selectedAccountUidRef.current]);
        }
        showAlert('관리자 화면구성·권한구성을 저장했습니다.', 'success');
      })
      .catch((e) => {
        showAlert(e?.message || '저장에 실패했습니다.', 'error');
      });
  }, [
    loadRightPanels,
    mergeCurrentPanels,
    programCatalog,
    rebuildBaseline,
    syncUserEditMarks,
    tenantCode,
  ]);

  const searchFields = useMemo(
    () => [
      [
        {
          name: 'loginId',
          label: '사용자ID',
          type: 'text',
          width: CommonCss.w100,
          labelWidth: SEARCH_LABEL_WIDTH,
        },
        {
          name: 'userNm',
          label: '사용자명',
          type: 'text',
          width: CommonCss.w100,
          labelWidth: SEARCH_LABEL_WIDTH,
        },
        {
          name: 'status',
          label: '계정상태',
          type: 'select',
          width: CommonCss.w100,
          labelWidth: SEARCH_LABEL_WIDTH,
          options: [{ value: '', label: '전체' }, ...ACCOUNT_STATUS_OPTIONS],
        },
      ],
    ],
    []
  );

  useRegisterGridExport({
    id: USER_GRID_ID,
    label: '사용자',
    getData: () =>
      userGridRef.current?.getFilteredData?.() ?? userGridRef.current?.getData?.() ?? [],
    getDataColumns: () => userGridRef.current?.getDataColumns?.() ?? [],
    joinKeys: ['accountUid'],
    order: 0,
  });
  useRegisterGridExport({
    id: SCREEN_GRID_ID,
    label: '화면구성',
    getData: () =>
      screenGridRef.current?.getFilteredData?.() ?? screenGridRef.current?.getData?.() ?? [],
    getDataColumns: () => screenGridRef.current?.getDataColumns?.() ?? [],
    joinKeys: ['accountUid', 'screenId'],
    order: 1,
  });
  useRegisterGridExport({
    id: PERMISSION_GRID_ID,
    label: '권한구성',
    getData: () =>
      permissionGridRef.current?.getFilteredData?.() ??
      permissionGridRef.current?.getData?.() ??
      [],
    getDataColumns: () => permissionGridRef.current?.getDataColumns?.() ?? [],
    joinKeys: ['accountUid', 'moduleCode', 'menuLevel'],
    order: 2,
  });

  const { openDialog: openExcelDialog, dialog: excelDialog } = useExcelExportAction({
    fileName: '관리자권한설정',
  });

  const actionButtons = useMemo(
    () => [
      { id: 'search', label: '조회', icon: '🔍', onClick: handleSearch, hotkey: 'F7' },
      {
        id: 'save',
        label: '저장',
        icon: '✅',
        onClick: handleSave,
        hotkey: 'F10',
        variant: 'success',
      },
      { id: 'excel', label: '엑셀', icon: '📥', onClick: openExcelDialog, hotkey: 'F11' },
    ],
    [handleSave, handleSearch, openExcelDialog]
  );

  const extraButtons = useMemo(() => {
    if (activeMode === 'screen') {
      return [
        {
          id: 'screen-toggle-all',
          label: screenAllOn ? '화면 전체 OFF' : '화면 전체 ON',
          icon: screenAllOn ? '☐' : '☑',
          onClick: handleToggleAllScreens,
          variant: screenAllOn ? 'danger' : undefined,
        },
      ];
    }
    return [
      {
        id: 'toggle-read',
        label: '조회 전체',
        icon: permReadOn ? '☑' : '☐',
        onClick: () => handleToggleMenuLevel('READ'),
        title: permReadOn ? '전 모듈 조회 해제' : '전 모듈 조회 부여',
      },
      {
        id: 'toggle-reg',
        label: '등록 전체',
        icon: permRegOn ? '☑' : '☐',
        onClick: () => handleToggleMenuLevel('REGISTER'),
        title: permRegOn ? '전 모듈 등록 해제' : '전 모듈 등록 부여',
      },
      {
        id: 'toggle-full',
        label: '권한 전체',
        icon: permFullOn ? '☑' : '☐',
        onClick: () => handleToggleMenuLevel('FULL'),
        title: permFullOn ? '전 모듈 전체권한 해제' : '전 모듈 전체권한 부여',
      },
    ];
  }, [
    activeMode,
    handleToggleAllScreens,
    handleToggleMenuLevel,
    permFullOn,
    permReadOn,
    permRegOn,
    screenAllOn,
  ]);

  const lookupColumns = useMemo(
    () => [
      hiCol.roTextColumn({ header: '부서코드', field: 'deptCode', size: 110 }),
      hiCol.roTextColumn({ header: '부서명', field: 'deptName', size: 160 }),
      hiCol.roTextColumn({ header: '경로', field: 'pathCode', size: 200 }),
    ],
    []
  );
  const lookupRows = useMemo(
    () => deptRows.map((r) => ({ ...r, id: r.deptUid })),
    [deptRows]
  );

  if (!isMaster) {
    return (
      <PageContainer>
        <PageTitle>관리자권한설정</PageTitle>
        <Meta>기관 관리자 전용 화면입니다. 접근 권한이 없습니다.</Meta>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <ActionButtonGroup buttons={actionButtons} extraButtons={extraButtons} />
        <CollapsibleSearchPanel
          storageKey="master-admin-permission"
          prefsUserId={prefsUserId}
          summary="사용자 조회"
        >
          <FormArea
            fields={searchFields}
            compact
            onFieldEnter={handleSearch}
            defaultValues={searchDefaults}
          />
        </CollapsibleSearchPanel>
      </FormProvider>

      <Meta>
        관리자 전용 · ① 화면구성(접근 화면) → ② 권한구성(조회·등록·전체)
        {' · '}버튼 단위 예외는 「예외권한설정」에서 처리
      </Meta>

      <Split>
        <Panel $flex={1.15}>
          <ActiveGridHeader gridId={USER_GRID_ID} extra="변경 시 좌측 A/U">
            사용자
          </ActiveGridHeader>
          <GridBody>
            <ActiveGridScope
              gridId={USER_GRID_ID}
              label="사용자"
              order={0}
              canAdd={false}
              canDelete={false}
              style={{ flex: 1, minHeight: 0, height: '100%' }}
            >
              <GridExportFocusScope
                gridId={USER_GRID_ID}
                style={{ flex: 1, minHeight: 0, height: '100%' }}
              >
                <HiGridComponent
                  ref={userGridRef}
                  columns={userColumns}
                  height="100%"
                  showRowNumber
                  rowNumberSize={56}
                  showModColumn
                  headerActive={activeGridId === USER_GRID_ID}
                  enableColumnFilters
                  enableHeaderMenu
                  enableSelectionStats
                  enableFreeze
                  enableQuickExport
                  exportFileName="사용자"
                  gridId={USER_GRID_ID}
                  prefsUserId={prefsUserId}
                />
              </GridExportFocusScope>
            </ActiveGridScope>
          </GridBody>
        </Panel>

        <Panel $flex={1.4}>
          <ModeTabBar>
            <ModeTab
              type="button"
              $active={activeMode === 'screen'}
              onClick={() => handleModeChange('screen')}
            >
              화면구성
            </ModeTab>
            <ModeTab
              type="button"
              $active={activeMode === 'permission'}
              onClick={() => handleModeChange('permission')}
            >
              권한구성
            </ModeTab>
          </ModeTabBar>

          <RightStack>
            <Meta style={{ padding: '4px 12px' }}>{selectedHint}</Meta>

            <div
              style={{
                display: activeMode === 'screen' ? 'flex' : 'none',
                flex: 1,
                minHeight: 0,
                flexDirection: 'column',
              }}
            >
              <ActiveGridHeader
                gridId={SCREEN_GRID_ID}
                extra="트리 체크 · 상위 선택 시 하위 일괄"
              >
                화면구성
              </ActiveGridHeader>
              <GridBody>
                <ActiveGridScope
                  gridId={SCREEN_GRID_ID}
                  label="화면구성"
                  order={1}
                  canAdd={false}
                  canDelete={false}
                  style={{ flex: 1, minHeight: 0, height: '100%' }}
                >
                  <GridExportFocusScope
                    gridId={SCREEN_GRID_ID}
                    style={{ flex: 1, minHeight: 0, height: '100%' }}
                  >
                    <HiGridComponent
                      ref={screenGridRef}
                      columns={screenColumns}
                      height="100%"
                      showRowNumber
                      rowNumberSize={56}
                      headerActive={activeGridId === SCREEN_GRID_ID}
                      treeOptions={screenTreeOptions}
                      {...screenGrid.contextMenuProps}
                      enableColumnFilters
                      enableHeaderMenu
                      enableSelectionStats
                      enableFreeze
                      enableQuickExport
                      exportFileName="관리자화면구성"
                      gridId={SCREEN_GRID_ID}
                      prefsUserId={prefsUserId}
                      colOptions={screenGrid.colOptions}
                    />
                  </GridExportFocusScope>
                </ActiveGridScope>
              </GridBody>
              <Guide>
                관리자가 접근·승인 처리할 화면을 선택합니다. 상위 메뉴 체크 시 하위 화면이 모두
                선택됩니다.
              </Guide>
            </div>

            <div
              style={{
                display: activeMode === 'permission' ? 'flex' : 'none',
                flex: 1,
                minHeight: 0,
                flexDirection: 'column',
              }}
            >
              <ActiveGridHeader gridId={PERMISSION_GRID_ID} extra="부여 체크 · F10 저장">
                권한구성
              </ActiveGridHeader>
              <GridBody>
                <ActiveGridScope
                  gridId={PERMISSION_GRID_ID}
                  label="권한구성"
                  order={2}
                  canAdd={false}
                  canDelete={false}
                  style={{ flex: 1, minHeight: 0, height: '100%' }}
                >
                  <GridExportFocusScope
                    gridId={PERMISSION_GRID_ID}
                    style={{ flex: 1, minHeight: 0, height: '100%' }}
                  >
                    <HiGridComponent
                      ref={permissionGridRef}
                      columns={permissionColumns}
                      height="100%"
                      showRowNumber
                      rowNumberSize={56}
                      headerActive={activeGridId === PERMISSION_GRID_ID}
                      {...permissionGrid.contextMenuProps}
                      enableColumnFilters
                      enableHeaderMenu
                      enableSelectionStats
                      enableFreeze
                      enableQuickExport
                      exportFileName="관리자권한구성"
                      gridId={PERMISSION_GRID_ID}
                      prefsUserId={prefsUserId}
                      colOptions={permissionGrid.colOptions}
                    />
                  </GridExportFocusScope>
                </ActiveGridScope>
              </GridBody>
              <Guide>
                선택한 화면에 대해 데이터 접근 수준(조회·등록·전체)과 데이터범위를 지정합니다.
              </Guide>
            </div>
          </RightStack>
        </Panel>
      </Split>

      <CodeLookupDialog
        open={deptLookupOpen}
        title="데이터범위 대상부서 선택"
        rows={lookupRows}
        columns={lookupColumns}
        enableSearch
        searchConfig={{
          codeFields: ['deptCode'],
          nameFields: ['deptName'],
          codeLabel: '부서코드',
          nameLabel: '부서명',
        }}
        onSelect={applyDeptLookup}
        onClose={() => setDeptLookupOpen(false)}
      />
      {excelDialog}
    </PageContainer>
  );
}

export default function AdminPermissionManage() {
  return (
    <GridExportProvider>
      <ActiveGridProvider>
        <AdminPermissionManageInner />
      </ActiveGridProvider>
    </GridExportProvider>
  );
}
